/**
 * SignalRManager - 全局单例的SignalR管理器
 * 提供链式调用API，支持统一的初始化、连接、订阅和数据分发
 */
import { SignalRClient } from './signalr-client';

class SignalRManager {
  constructor() {
    this._client = null;
    this._messageHandlers = new Map(); // 按照消息类型存储处理器
    this._error = null;
    this._options = {
      autoReconnect: true,
      maxReconnectAttempts: 999999,
      reconnectInterval: 5 // 秒
    };
    this._connectionParams = {
      url: '',
      queryParams: {}
    };
    this._isInitialized = false;
    this._retryIntervals = {
      connect: 5000, // 连接重试间隔，5秒
      subscribe: 5000 // 订阅重试间隔，5秒
    };
    this._retryTasks = new Map(); // 存储重试任务
  }

  /**
   * 获取SignalRManager实例
   * @returns {SignalRManager} 单例实例
   */
  static getInstance() {
    if (!SignalRManager._instance) {
      SignalRManager._instance = new SignalRManager();
    }
    return SignalRManager._instance;
  }

  /**
   * 初始化SignalR管理器
   * @param {Object} options - 配置选项
   * @returns {SignalRManager} - 当前实例，支持链式调用
   */
  init(options = {}) {
    if (this._isInitialized) {
      console.log('SignalRManager已经初始化');
      return this;
    }

    this._options = {
      ...this._options,
      ...options
    };

    this._client = new SignalRClient(this._options);
    
    // 注册全局消息处理器
    this._client.addMessageReceivedHandler(message => {
      this._handleMessage(message);
    });
    
    // 注册连接状态变化处理器
    this._client.addConnectionStatusChangedHandler((status, connectionId, detail) => {
      if (status) {
        console.log(`SignalR已连接，连接ID: ${connectionId}`);
      } else {
        console.error(`SignalR连接断开: ${detail}`);
        this._error = detail;
      }
      
      // 触发连接状态处理器
      if (this._connectionStatusHandler) {
        this._connectionStatusHandler(status, connectionId, detail);
      }
    });
    
    this._isInitialized = true;
    console.log('SignalRManager初始化完成');
    return this;
  }

  /**
   * 确保SignalR已初始化并连接
   * 如果未初始化则初始化，如果未连接则连接
   * @param {string} url - 服务器URL
   * @param {Object} options - 初始化选项
   * @param {Object} queryParams - 查询参数
   * @returns {Promise<SignalRManager>} - 返回Promise，解析为当前实例
   */
  async ensureConnection(url, options = {}, queryParams = {}) {
    // 如果未初始化，先初始化
    if (!this._isInitialized) {
      this.init(options);
    }
    
    // 如果未连接，则连接
    if (!this.isConnected) {
      await this.connect(url, queryParams);
    }
    
    return this;
  }

  /**
   * 连接到SignalR服务器
   * @param {string} url - 服务器URL
   * @param {Object} queryParams - 查询参数
   * @returns {Promise<SignalRManager>} - 返回Promise，解析为当前实例
   */
  async connect(url, queryParams = {}) {
    if (!this._isInitialized) {
      this.init();
    }
    
    // 如果已连接，直接返回
    if (this.isConnected) {
      console.log('SignalR已连接，跳过连接');
      return this;
    }
    
    this._connectionParams = { url, queryParams };
    
    try {
      await this._client.connect(url, queryParams);
      // 连接成功，取消可能存在的重试任务
      this._cancelRetryTask('connect');
      return this;
    } catch (error) {
      this._error = error.message;
      console.error('SignalR连接失败:', error);
      // 启动连接重试
      this._startConnectRetry();
      throw error;
    }
  }

  /**
   * 开始连接重试任务
   * @private
   */
  _startConnectRetry() {
    // 如果已经有重试任务，不重复创建
    if (this._retryTasks.has('connect')) {
      return;
    }

    console.log(`SignalR连接失败，${this._retryIntervals.connect/1000}秒后重试...`);
    
    const retryTask = setTimeout(async () => {
      console.log('SignalR尝试重新连接...');
      try {
        if (!this.isConnected && this._connectionParams.url) {
          await this._client.connect(this._connectionParams.url, this._connectionParams.queryParams);
          console.log('SignalR重连成功！');
          this._cancelRetryTask('connect');
        }
      } catch (error) {
        console.error('SignalR重连失败:', error);
        // 继续重试
        this._startConnectRetry();
      }
    }, this._retryIntervals.connect);
    
    this._retryTasks.set('connect', retryTask);
  }

  /**
   * 重新连接到SignalR服务器
   * @returns {Promise<SignalRManager>} - 返回Promise，解析为当前实例
   */
  async reconnect() {
    if (!this._connectionParams.url) {
      throw new Error('尚未设置连接参数，无法重连');
    }
    
    return this.connect(this._connectionParams.url, this._connectionParams.queryParams);
  }

  /**
   * 基于桥梁ID断开并重连，携带新的clientId
   * @param {string|number} bridgeId - 当前桥梁ID（单个）
   * @param {Object} extraQueryParams - 额外的查询参数
   * @returns {Promise<SignalRManager>}
   */
  async reconnectWithBridge(bridgeId, extraQueryParams = {}) {
    if (!bridgeId) {
      throw new Error('bridgeId不能为空');
    }

    if (!this._connectionParams.url && !this._client?._serverUrl) {
      throw new Error('尚未建立过连接，无法重连，请先调用connect');
    }

    // 优先使用已知的连接URL
    const url = this._connectionParams.url || this._client._serverUrl;
    const mergedQuery = {
      ...(this._connectionParams.queryParams || {}),
      ...extraQueryParams,
      clientId: bridgeId.toString()
    };

    // 更新内部记录
    this._connectionParams = {
      url,
      queryParams: mergedQuery
    };

    // 更新客户端ID（用于生成查询参数）
    if (this._client && typeof this._client.setClientId === 'function') {
      this._client.setClientId(bridgeId.toString());
    }

    // 先断开当前连接
    if (this._client && this._client.isConnected) {
      await this._client.disconnect();
    }

    // 重新连接并注册身份
    await this.connect(url, mergedQuery);
    await this.refreshIdentity();
    return this;
  }

  /**
   * 订阅主题
   * @param {string|Array<string>} topics - 要订阅的主题或主题数组
   * @returns {Promise<SignalRManager>} - 返回Promise，解析为当前实例
   */
  async subscribe(topics) {
    if (!this._client) {
      throw new Error('SignalR客户端未初始化，请先调用init()和connect()');
    }
    
    if (!this._client.isConnected) {
      throw new Error('SignalR未连接，请先调用connect()');
    }
    
    const topicsArray = Array.isArray(topics) ? topics : [topics];
    const failedTopics = [];
    
    for (const topic of topicsArray) {
      try {
        await this._client.subscribe(topic);
        console.log(`成功订阅主题: ${topic}`);
        // 订阅成功，取消该主题可能存在的重试任务
        this._cancelRetryTask(`subscribe:${topic}`);
      } catch (error) {
        console.error(`订阅主题${topic}失败:`, error);
        failedTopics.push(topic);
        // 启动该主题的订阅重试
        this._startSubscribeRetry(topic);
      }
    }
    
    if (failedTopics.length > 0) {
      throw new Error(`订阅失败的主题: ${failedTopics.join(', ')}`);
    }
    
    return this;
  }

  /**
   * 开始主题订阅的重试任务
   * @param {string} topic - 要重试订阅的主题
   * @private
   */
  _startSubscribeRetry(topic) {
    const taskKey = `subscribe:${topic}`;
    
    // 如果已经有重试任务，不重复创建
    if (this._retryTasks.has(taskKey)) {
      return;
    }

    console.log(`主题 ${topic} 订阅失败，${this._retryIntervals.subscribe/1000}秒后重试...`);
    
    const retryTask = setTimeout(async () => {
      console.log(`尝试重新订阅主题: ${topic}...`);
      try {
        if (this.isConnected) {
          await this._client.subscribe(topic);
          console.log(`重新订阅主题 ${topic} 成功！`);
          this._cancelRetryTask(taskKey);
        } else {
          console.warn(`SignalR未连接，无法重试订阅主题 ${topic}`);
          // 如果连接本身断开，等待连接恢复后会重试订阅
          this._cancelRetryTask(taskKey);
        }
      } catch (error) {
        console.error(`重新订阅主题 ${topic} 失败:`, error);
        // 继续重试
        this._startSubscribeRetry(topic);
      }
    }, this._retryIntervals.subscribe);
    
    this._retryTasks.set(taskKey, retryTask);
  }

  /**
   * 取消重试任务
   * @param {string} taskKey - 任务键名
   * @private
   */
  _cancelRetryTask(taskKey) {
    if (this._retryTasks.has(taskKey)) {
      clearTimeout(this._retryTasks.get(taskKey));
      this._retryTasks.delete(taskKey);
    }
  }

  /**
   * 取消订阅主题
   * @param {string|Array<string>} topics - 要取消订阅的主题或主题数组
   * @returns {Promise<SignalRManager>} - 返回Promise，解析为当前实例
   */
  async unsubscribe(topics) {
    if (!this._client || !this._client.isConnected) {
      return this;
    }
    
    const topicsArray = Array.isArray(topics) ? topics : [topics];
    
    for (const topic of topicsArray) {
      try {
        await this._client.unsubscribe(topic);
        console.log(`成功取消订阅主题: ${topic}`);
        // 取消该主题可能存在的重试任务
        this._cancelRetryTask(`subscribe:${topic}`);
      } catch (error) {
        console.error(`取消订阅主题${topic}失败:`, error);
      }
    }
    
    return this;
  }

  /**
   * 加入群组
   * @param {string|Array<string>} groups - 要加入的群组或群组数组
   * @returns {Promise<SignalRManager>} - 返回Promise，解析为当前实例
   */
  async joinGroup(groups) {
    if (!this._client) {
      throw new Error('SignalR客户端未初始化，请先调用init()和connect()');
    }
    
    if (!this._client.isConnected) {
      throw new Error('SignalR未连接，请先调用connect()');
    }
    
    const groupsArray = Array.isArray(groups) ? groups : [groups];
    
    for (const group of groupsArray) {
      try {
        await this._client.joinGroup(group);
        console.log(`成功加入群组: ${group}`);
      } catch (error) {
        console.error(`加入群组${group}失败:`, error);
        throw error;
      }
    }
    
    return this;
  }

  /**
   * 离开群组
   * @param {string|Array<string>} groups - 要离开的群组或群组数组
   * @returns {Promise<SignalRManager>} - 返回Promise，解析为当前实例
   */
  async leaveGroup(groups) {
    if (!this._client || !this._client.isConnected) {
      return this;
    }
    
    const groupsArray = Array.isArray(groups) ? groups : [groups];
    
    for (const group of groupsArray) {
      try {
        await this._client.leaveGroup(group);
        console.log(`成功离开群组: ${group}`);
      } catch (error) {
        console.error(`离开群组${group}失败:`, error);
      }
    }
    
    return this;
  }

  /**
   * 注册消息处理器
   * @param {string} messageType - 消息类型
   * @param {Function} handler - 处理函数
   * @returns {SignalRManager} - 当前实例，支持链式调用
   */
  onMessage(messageType, handler) {
    if (!this._messageHandlers.has(messageType)) {
      this._messageHandlers.set(messageType, []);
    }
    
    this._messageHandlers.get(messageType).push(handler);
    return this;
  }

  /**
   * 注册连接状态变化处理器
   * @param {Function} handler - 处理函数，接收(isConnected, connectionId, detail)参数
   * @returns {SignalRManager} - 当前实例，支持链式调用
   */
  onConnectionStatusChanged(handler) {
    this._connectionStatusHandler = handler;
    return this;
  }

  /**
   * 处理接收到的消息
   * @param {Object} message - 接收到的消息
   * @private
   */
  _handleMessage(message) {
    try {
      // console.log('SignalRManager收到消息:', message);
      // if(message && message.type==='alert')
      //   console.log("【alert】",message);
      const messageType = message.type || 'unknown';
      
      // 解析payload
      let parsedData = null;
      if (message.payload) {
        try {
          // 尝试直接解析JSON
          if (typeof message.payload === 'string' && (
              message.payload.startsWith('{') || 
              message.payload.startsWith('[') ||
              message.payload.startsWith('"')
          )) {
            parsedData = JSON.parse(message.payload);
          } 
          // 尝试解析Base64编码的payload
          else {
            parsedData = SignalRManager.parsePayload(message.payload);
          }
        } catch (e) {
          console.warn(`无法解析消息payload:`, e);
          parsedData = message.payload; // 如果无法解析，保留原始payload
        }
      }
      
      // 创建带有解析数据的消息对象
      const enhancedMessage = {
        ...message,
        parsedData // 添加解析后的数据
      };
      
      // 调用全局消息处理器
      if (this._messageHandlers.has('*')) {
        this._messageHandlers.get('*').forEach(handler => {
          try {
            handler(enhancedMessage, parsedData);
          } catch (error) {
            console.error('执行全局消息处理器时出错:', error);
          }
        });
      }
      
      // 调用特定类型的消息处理器
      if (this._messageHandlers.has(messageType)) {
        this._messageHandlers.get(messageType).forEach(handler => {
          try {
            handler(enhancedMessage, parsedData);
          } catch (error) {
            console.error(`执行[${messageType}]类型消息处理器时出错:`, error);
          }
        });
      }
    } catch (error) {
      console.error('处理消息时出错:', error);
    }
  }

  /**
   * 向主题发送消息
   * @param {string} topic - 目标主题
   * @param {string} messageType - 消息类型
   * @param {*} payload - 消息内容
   * @param {Object} metadata - 元数据
   * @returns {Promise<Object>} - 返回服务器响应
   */
  async sendToTopic(topic, messageType, payload, metadata = {}) {
    if (!this._client || !this._client.isConnected) {
      throw new Error('SignalR未连接，请先调用connect()');
    }
    
    return this._client.sendToTopic(topic, messageType, payload, metadata);
  }

  /**
   * 向群组发送消息
   * @param {string} group - 目标群组
   * @param {string} messageType - 消息类型
   * @param {*} payload - 消息内容
   * @param {Object} metadata - 元数据
   * @returns {Promise<Object>} - 返回服务器响应
   */
  async sendToGroup(group, messageType, payload, metadata = {}) {
    if (!this._client || !this._client.isConnected) {
      throw new Error('SignalR未连接，请先调用connect()');
    }
    
    return this._client.sendToGroup(group, messageType, payload, metadata);
  }

  /**
   * 向特定客户端发送消息
   * @param {string} clientId - 目标客户端ID
   * @param {string} messageType - 消息类型
   * @param {*} payload - 消息内容
   * @param {Object} metadata - 元数据
   * @returns {Promise<Object>} - 返回服务器响应
   */
  async sendToClient(clientId, messageType, payload, metadata = {}) {
    if (!this._client || !this._client.isConnected) {
      throw new Error('SignalR未连接，请先调用connect()');
    }
    
    return this._client.sendToClient(clientId, messageType, payload, metadata);
  }

  /**
   * 获取连接ID
   * @returns {string|null} - 当前连接ID，如果未连接则为null
   */
  get connectionId() {
    return this._client ? this._client.connectionId : null;
  }

  /**
   * 获取客户端ID
   * @returns {string|null} - 当前客户端ID，如果未初始化则为null
   */
  get clientId() {
    return this._client ? this._client.clientId : null;
  }

  /**
   * 获取连接状态
   * @returns {boolean} - 是否已连接
   */
  get isConnected() {
    return this._client ? this._client.isConnected : false;
  }

  /**
   * 重新注册身份信息（用于登录后刷新身份）
   * @returns {Promise<void>}
   */
  async refreshIdentity() {
    if (this._client && this._client.isConnected) {
      try {
        await this._client._registerIdentityFromStorage();
        console.log('SignalRManager: 身份信息已重新注册');
      } catch (error) {
        console.error('SignalRManager: 重新注册身份信息失败', error);
      }
    }
  }

  /**
   * 关闭连接并清理资源
   * @returns {Promise<void>}
   */
  async close() {
    // 取消所有重试任务
    for (const taskKey of this._retryTasks.keys()) {
      this._cancelRetryTask(taskKey);
    }
    
    if (this._client) {
      await this._client.disconnect();
      this._client = null;
    }
    
    this._messageHandlers.clear();
    this._connectionStatusHandler = null;
    this._isInitialized = false;
    console.log('SignalRManager已关闭并清理资源');
  }

  /**
   * 解析Base64编码的payload（工具方法）
   * @param {string} payload - Base64编码的数据
   * @returns {Object|null} - 解析后的数据对象
   */
  static parsePayload(payload) {
    if (!payload) return null;
    
    try {
      // Base64解码
      const decoded = SignalRManager.base64ToString(payload);
      if (!decoded) return null;
      
      // 解析JSON
      return JSON.parse(decoded);
    } catch (error) {
      console.error('解析payload失败:', error);
      return null;
    }
  }

  /**
   * Base64解码（工具方法）
   * @param {string} base64Str - Base64编码的字符串
   * @returns {string|null} - 解码后的字符串
   */
  static base64ToString(base64Str) {
    try {
      return decodeURIComponent(Array.prototype.map.call(atob(base64Str), function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
    } catch (e) {
      console.error('Base64解码失败:', e);
      return null;
    }
  }
}

// 初始化单例实例
SignalRManager._instance = null;

// 导出单例的获取方法
export default SignalRManager.getInstance(); 